/**
 * @file IndividualTabsPage.js
 * @description Represents the page which handles individual record tabs.
 */

import React, {
    useState,
    useEffect,
    useRef,
    useContext,
    useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PersonalTab from './PersonalTab';
import JobTab from './JobTab';
import CustomFieldsTab from './CustomFieldsTab';
import EnrollmentTab from './EnrollmentTab';
import AccountInfoTab from './AccountInfoTab';
import IndividualDataPointsTab from './IndividualDataPointsTab';
import styles from './IndividualPage.module.scss';
import {
    getIndividualRecord,
    deleteIndividualRecord,
} from '../../services/individualService';
import { getClientOptions } from '../../services/clientService';
import Spinner from '../ui/spinners/Spinner';
import { useAuthContext } from '../../store/AuthContext';
import { getStatusClass, getTenantUserStatusName } from '../../utils/utils';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import {
    ClientOptionEnum,
    SecurityAttributes,
    TenantUserStatusEnum,
} from '../../utils/data/enums';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
/**
 * Checks if the account is disabled and shows toast message if so
 * @param {Object} tenantUser - The tenant user object
 * @returns {boolean} True if account is disabled
 */
const checkAccountDisabled = tenantUser => {
    if (tenantUser?.tenantUserStatusId === TenantUserStatusEnum.Disabled) {
        Toast.error(
            getText('msg_66aa8e9b0915'),
            {},
            getText('msg_8d884d6c8bcb'),
        );
        return true;
    }
    return false;
};

const TABS = {
    PERSONAL: 'Personal',
    JOB: 'Job',
    CUSTOM_FIELDS: 'Custom Fields',
    ENROLLMENT: 'Enrollment',
    DATA_POINTS: 'Data Points',
    ACCOUNT: 'Account',
};

const IndividualTabsPage = () => {
    const navigate = useNavigate();
    const { individualId } = useParams();
    const [activeTab, setActiveTab] = useState(TABS.PERSONAL);
    const [individualRecord, setIndividualRecord] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const {
        companyCode,
        hasAttribute,
        fieldNamingMap,
        defaultFieldNamingMap,
        enableEmailAndPhoneValidation,
    } = useAuthContext();
    const { showModal } = useContext(ModalContext);
    const [tenantUser, setTenantUser] = useState(null);
    const [dataPointsEnabled, setDataPointsEnabled] = useState(false);

    const personalTabRef = useRef();
    const jobTabRef = useRef();
    const customFieldsTabRef = useRef();
    const enrollmentTabRef = useRef();
    const accountInfoTabRef = useRef();

    const fetchIndividualData = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await getIndividualRecord(individualId);
            setIndividualRecord(data);
            setTenantUser(data.tenantUsers?.[0] || null);
            const dataPointsOptionResponse = await getClientOptions(0, [
                ClientOptionEnum.DataPointsEnabled,
            ]);
            const dataPointsOption = dataPointsOptionResponse?.find(
                item =>
                    item.clientOptionId === ClientOptionEnum.DataPointsEnabled,
            );
            setDataPointsEnabled(dataPointsOption?.value === 'true');
            setError(null);
        } catch (err) {
            console.error('Error retrieving individual record:', err);
            setError(
                'Could not fetch individual record. Please try again later.',
            );
        } finally {
            setIsLoading(false);
        }
    }, [individualId]);

    useEffect(() => {
        if (individualId) {
            fetchIndividualData();
        }
    }, [individualId, fetchIndividualData]);

    const returnToSearch = () => {
        navigate(ROUTES.INDIVIDUALS);
    };

    const refreshIndividualData = () => {
        fetchIndividualData();
    };

    const handleDeleteIndividual = async () => {
        try {
            setIsLoading(true);
            await deleteIndividualRecord(individualId);
            Toast.success(getText('msg_ccac169d3f95'));
            navigate(ROUTES.INDIVIDUALS);
        } catch (err) {
            console.error('Failed to delete individual:', err);
            Toast.error(getText('msg_7ee8d9732105'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = () => {
        if (checkAccountDisabled(tenantUser)) return;

        showModal({
            type: ModalTypes.ACTION,
            title: getText('msg_0478d807b2a1'),
            subtitle: `Are you sure you want to delete ${individualRecord?.firstName} ${individualRecord?.lastName}?`,
            text: getText('msg_951f495b3487'),
            onYes: handleDeleteIndividual,
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
    };

    const handleSave = () => {
        const isAccountTab = activeTab === TABS.ACCOUNT;
        const hasPassword =
            isAccountTab && accountInfoTabRef.current?.hasPassword();

        if (!hasPassword && checkAccountDisabled(tenantUser)) return;

        switch (activeTab) {
            case TABS.PERSONAL:
                personalTabRef.current?.handleSave();
                break;
            case TABS.JOB:
                jobTabRef.current?.handleSave();
                break;
            case TABS.CUSTOM_FIELDS:
                customFieldsTabRef.current?.handleSave();
                break;
            case TABS.ENROLLMENT:
                enrollmentTabRef.current?.handleSave();
                break;
            case TABS.ACCOUNT:
                accountInfoTabRef.current?.handleSave();
                break;
            default:
                break;
        }
    };

    const renderTabContent = () => {
        if (activeTab === TABS.ENROLLMENT) {
            return (
                <div className={styles.tabContentEnrollment}>
                    <EnrollmentTab
                        ref={enrollmentTabRef}
                        individualId={individualId}
                    />
                </div>
            );
        } else if (activeTab === TABS.PERSONAL) {
            return (
                <div className={styles.tabContent}>
                    <PersonalTab
                        ref={personalTabRef}
                        individualId={individualId}
                        individualRecord={individualRecord}
                        tenantUser={tenantUser}
                        onSave={refreshIndividualData}
                        fieldNamingMap={fieldNamingMap}
                        defaultFieldNamingMap={defaultFieldNamingMap}
                        enableEmailAndPhoneValidation={
                            enableEmailAndPhoneValidation
                        }
                    />
                </div>
            );
        } else if (activeTab === TABS.JOB) {
            return (
                <div className={styles.tabContent}>
                    <JobTab
                        ref={jobTabRef}
                        individualId={individualId}
                        individualRecord={individualRecord}
                        tenantUser={tenantUser}
                        onSave={refreshIndividualData}
                        fieldNamingMap={fieldNamingMap}
                        defaultFieldNamingMap={defaultFieldNamingMap}
                    />
                </div>
            );
        } else if (activeTab === TABS.CUSTOM_FIELDS) {
            return (
                <div className={styles.tabContent}>
                    <CustomFieldsTab
                        ref={customFieldsTabRef}
                        individualId={individualId}
                        individualRecord={individualRecord}
                        tenantUser={tenantUser}
                        onSave={refreshIndividualData}
                        fieldNamingMap={fieldNamingMap}
                        defaultFieldNamingMap={defaultFieldNamingMap}
                    />
                </div>
            );
        } else if (activeTab === TABS.DATA_POINTS) {
            return (
                <div className={styles.tabContent}>
                    <IndividualDataPointsTab individualId={individualId} />
                </div>
            );
        } else if (activeTab === TABS.ACCOUNT) {
            return (
                <div className={styles.tabContent}>
                    <AccountInfoTab
                        ref={accountInfoTabRef}
                        individualId={individualId}
                        individualRecord={individualRecord}
                        companyCode={companyCode}
                        onSave={refreshIndividualData}
                    />
                </div>
            );
        }

        return null;
    };

    if (error) {
        return (
            <div className={`page-content ${styles.container}`}>
                <div className="card">
                    <div className="card-body px-4 py-4">
                        <p className="text-danger">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center position-relative pb-4">
                        <p className="page-title float-start ms-4 d-inline-flex">
                            {individualRecord?.firstName}{' '}
                            {individualRecord?.lastName}
                            {individualRecord && (
                                <span
                                    className={`${getStatusClass(
                                        tenantUser?.tenantUserStatusId || 0,
                                    )} align-self-center ms-4`}
                                >
                                    {getTenantUserStatusName(
                                        tenantUser?.tenantUserStatusId,
                                    ) || 'Not Started'}
                                </span>
                            )}
                            {individualRecord?.isTestRecord && (
                                <span
                                    className={`align-self-center ms-4 ing-badge-inactive`}
                                >
                                    {getText('msg_640ab2bae07b')}
                                </span>
                            )}
                        </p>
                        <div className="float-end align-items-center me-4">
                            {hasAttribute(
                                SecurityAttributes.IndividualFullAccess,
                            ) && (
                                <>
                                    {hasAttribute(
                                        SecurityAttributes.IndividualDelete,
                                    ) && (
                                        <p
                                            type="button"
                                            onClick={handleDelete}
                                            className="ing-text-buttons d-inline me-3"
                                        >
                                            <i className="bi bi-trash me-2" />
                                            {getText('msg_f6fdbe48dc54')}
                                        </p>
                                    )}

                                    <p
                                        type="button"
                                        onClick={handleSave}
                                        className="ing-text-buttons d-inline mx-3"
                                    >
                                        <i className="bi bi-check-lg me-2" />
                                        {getText('msg_efc007a393f6')}
                                    </p>
                                </>
                            )}
                            <p
                                type="button"
                                onClick={returnToSearch}
                                className="ing-text-buttons d-inline ms-3"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </p>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center position-relative mt-4">
                        <ul className={`col-md-8 col-lg-8 ${styles.tabList}`}>
                            {Object.values(TABS)
                                .filter(
                                    tab =>
                                        tab !== TABS.DATA_POINTS ||
                                        dataPointsEnabled,
                                )
                                .map(tab => (
                                    <li
                                        key={tab}
                                        className={`${styles.tabItem} ${
                                            activeTab === tab
                                                ? styles.active
                                                : ''
                                        }`}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {tab}
                                    </li>
                                ))}
                        </ul>
                    </div>

                    <div className={`w-100 mt-5 pt-4 ${styles.tabsContainer}`}>
                        {isLoading ? (
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        ) : (
                            renderTabContent()
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndividualTabsPage;
